import { useEffect, useState } from 'react'
import { useAuroraStore } from '../store/auroraStore'
import { activeChild, gameRevision, madeGames } from '../store/selectors'
import { groupGames, pairSingles } from '../lib/games'
import { todayISO } from '../lib/dates'
import { useCards, useMinute } from '../components/cards/useCards'
import { NewDeckSheet } from '../components/cards/NewDeckSheet'
import { CardsSection } from '../components/cards/CardsSection'
import { TodayHero } from '../components/playroom/TodayHero'
import { ReadingPath } from '../components/playroom/ReadingPath'
import { MakeSomething } from '../components/playroom/MakeSomething'
import { GameRow } from '../components/playroom/GameRow'
import { usePath } from '../components/playroom/usePath'
import '../components/cards/cards.css'
import '../components/playroom/playroom.css'

/** How many of a skill's games show before "more": its newest, one row of the list. */
const FIRST = 2

/**
 * The Playroom: everything she plays, in one place.
 *
 * Read top to bottom in the order a parent needs it. First what to hand her
 * right now; then her reading path, the long journey the built-in games make;
 * then everything made just for her, with the way to make more; then her
 * decks and what she knows, for the evening.
 */
export function PlayroomView() {
  const child = useAuroraStore(activeChild) as any
  const cards = useCards()
  const path = usePath()
  const games = useAuroraStore(madeGames) as any[]
  const blocks = useAuroraStore((s: any) => s.blocks) as Record<string, any>
  // The ready games with a change on its way, as one string so the store hands back the same value.
  const changing = useAuroraStore((s: any) => games.filter((g) => gameRevision(s, g._id)).map((g) => g._id).join(' ')) as string
  // A minute's resolution moves a game out of "Just made" once it has been seen there a while.
  const minute = useMinute()
  const [newDeck, setNewDeck] = useState(false)
  const [unfolded, setUnfolded] = useState<Set<string>>(() => new Set())
  if (!child) return null
  const name = child.name ?? 'her'
  const making = games.filter((g) => g.generation?.status === 'generating').length
  const groups = groupGames(games, { today: todayISO(child.timezone), blocks, changing: new Set(changing.split(' ')), now: minute })
  const halves = pairSingles(groups)
  const unfold = (key: string) => setUnfolded((u) => new Set(u).add(key))

  return (
    <div className="page playroom cards-page">
      <header className="page-head reveal">
        <h1 className="page-title">{name}'s playroom</h1>
        <p className="page-sub">Everything she plays, in one place.</p>
      </header>

      <PlayIndex parts={[['part-today', 'Today'], ['part-path', 'Reading path'], ['part-made', `Made for ${name}`], ['part-cards', 'Cards']]} />

      <div id="part-today" className="play-part">
        <TodayHero cards={cards} next={path.next} childId={child._id} onNewDeck={() => setNewDeck(true)} />
      </div>

      <section id="part-path" className="section play-section play-part" aria-labelledby="play-path">
        <div className="section-head">
          <h2 className="section-title" id="play-path">Reading path</h2>
          <span className="section-note">From what she has actually read</span>
        </div>
        <ReadingPath standing={path} childId={child._id} />
      </section>

      <div className="play-cols">
        <section id="part-made" className="section play-section play-part" aria-labelledby="play-made">
          <div className="section-head">
            <h2 className="section-title" id="play-made">Made for {name}</h2>
            {games.length > 0 && (
              <span className="section-note tnum">
                {making ? `${making} being made · ` : ''}{games.length} in all
              </span>
            )}
          </div>
          <MakeSomething childId={child._id} name={name} />
          {games.length === 0 ? (
            <p className="play-empty">
              Nothing made for {name} yet. Describe one above, or tap Make it playable on any activity in the day.
            </p>
          ) : (
            <div className="game-groups">
            {groups.map((group, i) => {
              const all = group.open || unfolded.has(group.key)
              const shown = all ? group.games : group.games.slice(0, FIRST)
              return (
                <section key={group.key} id={`games-${group.key}`} className={`game-group${halves.has(i) ? ' is-half' : ''}`} aria-label={group.title || undefined}>
                  {group.title && (
                    <h3 className="game-group-title">
                      {group.title}
                      <span className="game-group-n tnum">{group.games.length}</span>
                    </h3>
                  )}
                  <ul className="game-list">
                    {shown.map((g, i) => (
                      <li key={g._id} className="reveal" style={{ '--i': Math.min(i, 8) } as React.CSSProperties}>
                        <GameRow artifact={g} listed />
                      </li>
                    ))}
                  </ul>
                  {shown.length < group.games.length && (
                    <button className="btn btn-quiet btn-sm play-more" onClick={() => unfold(group.key)}>
                      {group.games.length - shown.length} more
                    </button>
                  )}
                </section>
              )
            })}
            </div>
          )}
        </section>

        <section id="part-cards" className="section play-section play-part" aria-labelledby="play-cards">
          <div className="section-head">
            <h2 className="section-title" id="play-cards">Cards</h2>
          </div>
          <CardsSection cards={cards} onNewDeck={() => setNewDeck(true)} />
        </section>
      </div>

      {newDeck && <NewDeckSheet cards={cards} onClose={() => setNewDeck(false)} />}
    </div>
  )
}

/**
 * The Playroom's parts, held at the top of the page as it scrolls: the page
 * runs several screens, and her cards and what she knows (checked every
 * day) sit at its foot. One tap reaches any part, and the part on screen is
 * marked, so the parent always knows where on the page they are.
 */
/** How far below the top of the scrolling page a part counts as reached: just under the sticky index. */
const INDEX_LINE = 72

function PlayIndex({ parts }: { parts: Array<[string, string]> }) {
  const [here, setHere] = useState(parts[0]![0])
  const ids = parts.map(([id]) => id).join(' ')
  useEffect(() => {
    const main = document.querySelector<HTMLElement>('.shell-main')
    if (!main) return
    const list = ids.split(' ')
    // The part on screen is the last one whose top has reached the line just
    // under the index: a tall part stays marked until the next one arrives,
    // and once the page bottoms out the last part is marked, since a short
    // final part can never scroll up to the line. Scroll events already come
    // once a frame, and this reads four rectangles, so it runs on each.
    const mark = () => {
      if (main.scrollTop + main.clientHeight >= main.scrollHeight - 2) return setHere(list[list.length - 1]!)
      const line = main.getBoundingClientRect().top + INDEX_LINE
      let current = list[0]!
      for (const id of list) {
        const top = document.getElementById(id)?.getBoundingClientRect().top
        if (top !== undefined && top <= line) current = id
      }
      setHere(current)
    }
    mark()
    main.addEventListener('scroll', mark, { passive: true })
    window.addEventListener('resize', mark)
    return () => {
      main.removeEventListener('scroll', mark)
      window.removeEventListener('resize', mark)
    }
  }, [ids])
  const go = (id: string) => {
    setHere(id)
    document.getElementById(id)?.scrollIntoView({ block: 'start', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' })
  }
  return (
    <nav className="play-index" aria-label="Parts of the playroom">
      {parts.map(([id, label]) => (
        <button key={id} className={`play-index-item${here === id ? ' is-here' : ''}`} aria-current={here === id ? 'true' : undefined} onClick={() => go(id)}>
          {label}
        </button>
      ))}
    </nav>
  )
}
